import { defineStore } from 'pinia';
import { ref, reactive } from 'vue';
import api from '@/api';

export const useFilterStore = defineStore('filterStore', () => {
  const dynamicCounts = reactive({});

  const activeFilters = reactive({
    range_values: {},
    switch_values: {},
    text_values: {},
    select_values: [],
    district: [],
    multi_nested: '', // ✅ json string
    published: '',
    only_with_photo: false,
  });

  const categoryId = ref(null);
  const locationId = ref(null);

  // -----------------------------
  // CENTRALIZED OPTIONS LOADING (MultiSelect / Select)
  // -----------------------------
  const optionsByProperty = reactive({}); // { [propertyId]: [{id,name,...}, ...] }
  const optionsLoading = reactive({}); // { [propertyId]: true/false }
  const optionsInflight = new Map(); // защита от параллельных одинаковых запросов

  const OPTIONS_CACHE_PREFIX = 'select_values_cache_';
  const MAX_PAGES = 500;

  function optionsCacheKey(propertyId) {
    return `${OPTIONS_CACHE_PREFIX}${propertyId}`;
  }

  function readOptionsCache(propertyId) {
    try {
      const raw = localStorage.getItem(optionsCacheKey(propertyId));
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeOptionsCache(propertyId, items) {
    try {
      localStorage.setItem(optionsCacheKey(propertyId), JSON.stringify(items));
    } catch (e) {
      // если localStorage переполнен — просто не кэшируем
    }
  }

  async function loadAllSelectValues(propertyId, { include_children = true, force = false } = {}) {
    if (!propertyId) return [];

    // 1) memory cache
    if (
      !force &&
      Array.isArray(optionsByProperty[propertyId]) &&
      optionsByProperty[propertyId].length
    ) {
      return optionsByProperty[propertyId];
    }

    // 2) localStorage cache
    if (!force) {
      const cached = readOptionsCache(propertyId);
      if (Array.isArray(cached) && cached.length) {
        optionsByProperty[propertyId] = cached;
        return cached;
      }
    }

    // 3) inflight protection
    const inflightKey = `${propertyId}:${include_children}`;
    if (optionsInflight.has(inflightKey)) return optionsInflight.get(inflightKey);

    const p = (async () => {
      optionsLoading[propertyId] = true;
      const all = [];

      for (let page = 1; page <= MAX_PAGES; page++) {
        try {
          const { data } = await api.get(
            `attributes/select_values/?property=${propertyId}&include_children=${include_children}&page=${page}`,
          );

          const results = data?.results || [];

          // стоп если пусто
          if (!results.length) break;

          all.push(...results);

          // Важно: сервер у тебя НЕ отдаёт next, поэтому просто продолжаем
          // пока не упрёмся в 404 "Неправильная страница" (catch)
        } catch (e) {
          break;
        }
      }

      optionsByProperty[propertyId] = all;
      writeOptionsCache(propertyId, all);
      return all;
    })().finally(() => {
      optionsLoading[propertyId] = false;
      optionsInflight.delete(inflightKey);
    });

    optionsInflight.set(inflightKey, p);
    return p;
  }

  function setMeta({ category, location }) {
    categoryId.value = category;
    locationId.value = location;
  }

  function updateFilter({ type, property, value }) {
    if (type === 'only_with_photo') {
      activeFilters.only_with_photo = value === true;
      return;
    }

    if (type === 'range') {
      // value ожидаем как [A, B] или что-то вроде этого
      const toNumOrNull = v => {
        if (v === null || v === undefined || v === '') return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };

      let A = Array.isArray(value) ? toNumOrNull(value[0]) : null;
      let B = Array.isArray(value) ? toNumOrNull(value[1]) : null;

      // если оба пустые — удаляем фильтр, чтобы не слать range_filter_...=__ на бэкенд
      if (A === null && B === null) {
        delete activeFilters.range_values[property];
        return;
      }

      // ✅ если выбран обратный диапазон — автоматически исправляем
      if (A !== null && B !== null && A > B) {
        [A, B] = [B, A];
      }

      activeFilters.range_values[property] = [A, B];
      return;
    }

    if (type === 'switch' || type === 'boolean') {
      const isTrue =
        value === true ||
        value === 'true' ||
        value === 1 ||
        value === '1' ||
        value === 'on' ||
        value === 'yes';

      if (isTrue) {
        activeFilters.switch_values[property] = true; // храним строго boolean
      } else {
        delete activeFilters.switch_values[property]; // выключили => фильтра нет
      }
      return;
    }

    if (type === 'text') {
      activeFilters.text_values[property] = value;
      return;
    }

    if (type === 'select') {
      activeFilters.select_values = [
        ...activeFilters.select_values.filter(obj => obj.property !== property),
        { property, value },
      ];
      return;
    }

    if (type === 'district') {
      activeFilters.district = Array.isArray(value) ? value : [];
      return;
    }

    if (type === 'multi') {
      const ids = (Array.isArray(value) ? value : [])
        .map(v => (typeof v === 'object' && v !== null ? v.value : v))
        .map(v => parseInt(v))
        .filter(v => !Number.isNaN(v));

      activeFilters.select_values = [
        ...activeFilters.select_values.filter(obj => obj.property !== property),
        ...ids.map(id => ({ property, value: id })),
      ];
      return;
    }

    if (['nested', 'nested_parent', 'nested_children', 'nested_multi_select'].includes(type)) {
      if (Array.isArray(value)) {
        activeFilters.select_values = [
          ...activeFilters.select_values.filter(obj => obj.property !== property),
          ...value.map(val => ({ property, value: val })),
        ];
      } else if (value === null) {
        activeFilters.select_values = activeFilters.select_values.filter(
          obj => obj.property !== property,
        );
      } else {
        activeFilters.select_values = [
          ...activeFilters.select_values.filter(obj => obj.property !== property),
          { property, value },
        ];
      }
      return;
    }

    if (type === 'published') {
      activeFilters.published = value;
      return;
    }

    if (type === 'multi_nested') {
      activeFilters.multi_nested = value || '';
      return;
    }
  }

  function removeFilterByProperty(property) {
    activeFilters.select_values = activeFilters.select_values.filter(
      obj => obj.property !== property,
    );
  }

  function removeChildValues(property, values = []) {
    activeFilters.select_values = activeFilters.select_values.filter(
      obj => obj.property !== property || !values.includes(obj.value),
    );
  }

  async function fetchDynamicCount({ property = null, data_type = null, mode = 'exclude' } = {}) {
    const params = {
      category: categoryId.value,
      location: locationId.value,
    };

    if (Array.isArray(activeFilters.district) && activeFilters.district.length) {
      params.district = activeFilters.district;
    }

    if (activeFilters.only_with_photo === true) {
      params.only_with_photo = true;
    }

    // ---------- RANGE ----------
    Object.entries(activeFilters.range_values).forEach(([property, range]) => {
      if (!Array.isArray(range)) return;

      const A = range[0] ?? '';
      const B = range[1] ?? '';

      // если оба пустые — не шлём вообще
      if (A === '' && B === '') return;

      params[`range_filter_${property}__range`] = `${A}__${B}`;
    });

    // ---------- SWITCH / BOOLEAN ----------
    Object.entries(activeFilters.switch_values).forEach(([property, value]) => {
      if (value === true) {
        params[`switch_${property}`] = true;
      }
    });

    // ---------- TEXT ----------
    Object.entries(activeFilters.text_values).forEach(([property, value]) => {
      if (value) {
        params[`text_${property}`] = value;
      }
    });

    // ---------- SELECT / MULTI / NESTED ----------
    const selectValues = activeFilters.select_values
      .map(obj =>
        typeof obj.value === 'object' && obj.value !== null ? obj.value.value : obj.value,
      )
      .map(v => parseInt(v))
      .filter(v => !Number.isNaN(v));

    if (selectValues.length) {
      params.select_values = selectValues;
    }

    // ---------- PUBLISHED ----------
    if (activeFilters.published) {
      params.published = activeFilters.published;
    }

    if (activeFilters.multi_nested) {
      params.multi_nested = activeFilters.multi_nested;
    }

    // ---------- TYPE (exclude current filter) ----------
    // mode='include' => считаем TOTAL (ничего не исключаем)
    if (mode === 'exclude' && data_type && property !== null) {
      if (data_type === 'range') {
        params.type = `range_filter_${property}__range`;
      } else if (data_type === 'switch' || data_type === 'boolean') {
        params.type = `switch_${property}`;
      } else if (data_type === 'text') {
        params.type = `text_${property}`;
      } else if (
        [
          'select',
          'multi',
          'nested',
          'nested_parent',
          'nested_children',
          'nested_multi_select',
        ].includes(data_type)
      ) {
        params.type = 'select_values';
        params.property_id = property;
      } else if (data_type === 'published') {
        params.type = 'published';
      } else if (data_type === 'multi_nested') {
        params.type = 'multi_nested';
      }
    }

    // ---------- _ts ----------
    params._ts = Date.now();

    try {
      const { data } = await api.get('publications/publications/dynamic_count/', {
        params,
        paramsSerializer: { indexes: null }, // ✅ массивы без []
      });

      // ✅ TOTAL для кнопки
      if (mode === 'include' || !params.type) {
        dynamicCounts.__total__ = data.count;
      } else {
        // optional: если когда-то понадобится где-то показать exclude-значение
        dynamicCounts.__exclude__ = data.count;
      }
    } catch (e) {
      console.warn('Failed to fetch dynamic count:', e);
    }
  }

  function resetFilters() {
    Object.keys(activeFilters.range_values).forEach(k => delete activeFilters.range_values[k]);
    Object.keys(activeFilters.switch_values).forEach(k => delete activeFilters.switch_values[k]);
    Object.keys(activeFilters.text_values).forEach(k => delete activeFilters.text_values[k]);

    activeFilters.select_values.splice(0);
    activeFilters.district.splice(0); // ✅ ДОБАВЬ
    activeFilters.multi_nested = '';
    activeFilters.published = '';
    activeFilters.only_with_photo = false;

    delete dynamicCounts.__total__;
  }

  return {
    dynamicCounts,
    activeFilters,
    setMeta,
    updateFilter,
    fetchDynamicCount,
    removeFilterByProperty,
    removeChildValues,
    resetFilters,

    // options
    optionsByProperty,
    optionsLoading,
    loadAllSelectValues,
  };
});
